/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DynamicFormRenderer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { ComponentRegistry } from "./registry";
import { PAGE_SIZES } from "./pageSize";
import { componentStyle } from "../../../config/appTheme";
import ReportDesigner from "./ReportDesigner";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttributeUpdateDialog from "../../labcar/Attributes/AttributeUpdateDialog";

function updateElements(elements, elementId, value) {
  return elements.map((el) => {
    if (el.id === elementId) {
      return {
        ...el,
        properties: {
          ...el.properties,
          value,
        },
      };
    }
    return el;
  });
}

function updatePages(fields, pageIndex, elementId, value) {
  return fields.map((page, pIdx) => {
    if (pIdx !== pageIndex) return page;

    return {
      ...page,
      elements: updateElements(page.elements, elementId, value),
    };
  });
}

export default function DynamicFormRenderer({
  isOpen,
  onClose,
  data,
  setDynamicFormData,
  setDynamicFormFilteredData,
  mode = "CREATE", // CREATE | UPDATE
  attributeList,
  selectedComponent,
}) {
  const [template, setTemplate] = useState(data || { fields: [] });
  const [editForm, setEditForm] = useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);
  const pageRefs = useRef([]);

  const initialValuesRef = useRef({});

  useEffect(() => {
    if (!data) return;

    setTemplate(data);

    if (mode === "UPDATE") {
      initialValuesRef.current = Object.fromEntries(
        (data.fields ?? []).flatMap((page, pageIndex) =>
          (page.elements ?? []).map((el) => [
            `${pageIndex}_${el.id}`,
            el?.properties?.value ?? "",
          ]),
        ),
      );
    } else {
      initialValuesRef.current = {};
    }
  }, [data, mode]);

  const updateElementValue = useCallback((pageIndex, elementId, value) => {
    setTemplate((prev) => ({
      ...prev,
      fields: updatePages(prev.fields, pageIndex, elementId, value),
    }));
  }, []);

  const handleSave = useCallback(() => {
    if (editForm) {
      setTemplate((prev) => prev);
    } else {
      setDynamicFormData(template);
      setDynamicFormFilteredData(mapIdToMasterTemplateId(template));
      onClose();
    }
  }, [editForm, template, setDynamicFormData, setDynamicFormFilteredData, onClose]);

  const handelEditForm = () => {
    setEditForm(!editForm);
  };

  const getSelectedComponentType = () => {
    if (selectedComponent?.swm_id) return "swm";
    if (selectedComponent?.ee_id) return "ee";
    if (selectedComponent?.vc_id) return "vc";
    if (selectedComponent?.vs_id) return "vs";
    return "ee";
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{
          position: "relative",
          p: 2,
          borderBottom: "none",
        }}
      >
        <Box sx={{ ...componentStyle.font.cardHeadder }}>
          {`${template?.name ? template?.name : "Dynamic Form"}`}
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 40,
            top: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            {!editForm && (
              <>
                <IconButton
                  onClick={() => {
                    if (selectedComponent) {
                      setOpenAttributeUpdateDialog(true);
                    }
                  }}
                  disabled={!selectedComponent}
                  aria-label="copy attributes"
                >
                  <VisibilityOutlinedIcon />
                </IconButton>

                <CustomButtonV2
                  type="submit"
                  size="small"
                  btnName="Edit Form"
                  handleOnClick={handelEditForm}
                />
              </>
            )}
          </Box>
        </Box>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            position: "absolute",
            right: 2,
            top: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: 0, margin: 0 }}>
        {editForm ? (
          <ReportDesigner
            type="update"
            elements={template?.fields}
            onSave={(updatedPages) => {
              setTemplate((prev) => ({
                ...prev,
                fields: updatedPages,
              }));
              setEditForm(false);
            }}
          />
        ) : (
          template?.fields?.map((page, pageIndex) => {
            let pxW = PAGE_SIZES[page.settings.pageSize]?.width || 794;
            let pxH = PAGE_SIZES[page.settings.pageSize]?.height || 1123;

            if (page.settings.orientation === "Landscape") {
              [pxW, pxH] = [pxH, pxW];
            }

            return (
              <div
                key={page?.id || page?.page_id || `page_${pageIndex}`}
                ref={(el) => (pageRefs.current[pageIndex] = el)}
                style={{
                  position: "relative",
                  width: pxW,
                  height: pxH,
                  margin: "20px auto",
                  background: page.settings.backgroundColor,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  boxShadow: "0 4px 4px rgba(0,0,0,0.15)",
                }}
              >
                {page.elements.map((el) => {
                  const reg = ComponentRegistry[el.type];
                  if (!reg?.render) return null;

                  const initialValue =
                    mode === "UPDATE"
                      ? (initialValuesRef.current[`${pageIndex}_${el.id}`] ??
                        "")
                      : "";

                  return (
                    <div
                      key={el.id}
                      style={{
                        position: "absolute",
                        top: el.y,
                        left: el.x,
                        width: el.width,
                        height: el.height,
                      }}
                    >
                      {reg.render(
                        el,
                        el.properties?.value,
                        (v) => updateElementValue(pageIndex, el.id, v),
                        initialValue,
                        false,
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <CustomButtonV2
          type="cancel"
          size="small"
          btnName="Close"
          handleOnClick={onClose}
        />
        <CustomButtonV2
          type="submit"
          size="small"
          btnName={editForm ? "Back" : "Save"}
          handleOnClick={editForm ? handelEditForm : handleSave}
        />
      </DialogActions>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        onClickSuccess={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        selectedComponent={selectedComponent}
        componentType={getSelectedComponentType()}
        type="COPY"
        hideDefaultActions
      />
    </Dialog>
  );
}

const mapIdToMasterTemplateId = (template) => {
  if (!template) return template;

  const {
    id,
    name,
    type,
    template_tag,
    fields,
    headers,
    footers,
    signatures,
    status,
  } = template;

  return {
    master_template_id: id,
    name,
    type,
    template_tag,
    fields,
    headers,
    footers,
    signatures,
    status,
  };
};

DynamicFormRenderer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    template_tag: PropTypes.string,
    fields: PropTypes.array,
    headers: PropTypes.array,
    footers: PropTypes.array,
    signatures: PropTypes.array,
    status: PropTypes.string,
  }),
  setDynamicFormData: PropTypes.func,
  setDynamicFormFilteredData: PropTypes.func,
  mode: PropTypes.string,
  attributeList: PropTypes.array,
  selectedComponent: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vc_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vs_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
